import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  NativeSyntheticEvent,
  Pressable,
  StyleSheet,
  Text,
  TextLayoutEventData,
  View,
} from 'react-native';
import { Link } from '../crop-circle/Link';
import { ChevronDownIcon, ChevronUpIcon } from '../crop-circle/icons';
import { tokens } from '../crop-circle/tokens';
import { themedStyleSheet } from '../../themes/themedStyleSheet';
import { Colors } from '../../themes/colors';

const CHEVRON_SIZE = 16;
const CHEVRON_GAP = tokens.spacing.xs;

type AssetDescriptionProps = {
  description?: string;
  /** Shown as "+ Add description" when there is no description; omit to show nothing. */
  onAddPress?: () => void;
};

export const AssetDescription = ({
  description,
  onAddPress,
}: AssetDescriptionProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const text = description?.trim();

  if (!text) {
    return onAddPress ? (
      <Link
        onPress={onAddPress}
        underline={false}
        emphasised
        accessibilityRole="button"
        style={styles.addLink}
        textStyle={themed.addLinkText}
        testID="asset-description-add"
      >
        {`+ ${t('asset.header.addDescription')}`}
      </Link>
    ) : null;
  }

  // The chevron only appears once the text overflows, so measure against the
  // width the text gets WITH the chevron beside it; measuring at full width
  // would let a line that fits only without the chevron flip it on and off.
  const onMeasureLayout = (e: NativeSyntheticEvent<TextLayoutEventData>) =>
    setIsTruncated(e.nativeEvent.lines.length > 1);

  const canToggle = isTruncated || expanded;
  const Chevron = expanded ? ChevronUpIcon : ChevronDownIcon;

  return (
    <View style={styles.container}>
      <Text
        style={[themed.text, styles.measure]}
        onTextLayout={onMeasureLayout}
        aria-hidden
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
        testID="asset-description-measure"
      >
        {text}
      </Text>
      <Pressable
        style={styles.row}
        onPress={canToggle ? () => setExpanded((v) => !v) : undefined}
        disabled={!canToggle}
        accessibilityRole={canToggle ? 'button' : 'text'}
        accessibilityState={canToggle ? { expanded } : undefined}
        accessibilityHint={
          canToggle
            ? t(
                expanded
                  ? 'asset.header.collapseDescription'
                  : 'asset.header.expandDescription'
              )
            : undefined
        }
        testID="asset-description"
      >
        <Text
          style={[themed.text, styles.text]}
          numberOfLines={expanded ? undefined : 1}
          testID="asset-description-text"
        >
          {text}
        </Text>
        {canToggle && (
          <Chevron
            size={CHEVRON_SIZE}
            color={Colors.role.textSecondary}
            style={styles.chevron}
            testID="asset-description-toggle"
          />
        )}
      </Pressable>
    </View>
  );
};

const themed = themedStyleSheet((role) => ({
  text: {
    ...tokens.typography.body.small,
    color: role.textPrimary,
  },
  // Matches the asset page's "View All" section links.
  addLinkText: {
    color: role.textSoft,
  },
}));

const styles = StyleSheet.create({
  container: {
    width: '100%',
    marginBottom: tokens.spacing.xs,
  },
  addLink: {
    alignSelf: 'flex-start',
    marginBottom: tokens.spacing.xs,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    columnGap: CHEVRON_GAP,
  },
  text: {
    flex: 1,
    flexShrink: 1,
  },
  measure: {
    position: 'absolute',
    left: 0,
    right: CHEVRON_SIZE + CHEVRON_GAP,
    opacity: 0,
  },
  chevron: {
    // Centres the 16px glyph on the first 20px line of body-small.
    marginTop: 2,
  },
});
